import { Text, useToast } from '@chakra-ui/core'
import React from 'react'
import {
  Maybe,
  EvidenceEntity,
  useCreateBaoverifierFullTaskFromDesktopMutation,
  Enum_Task_Status,
  TaskDefinitionEntity,
  UploadFileEntity
} from '../../../../generated/graphql'
import { Formik, Form } from 'formik'
import { ConnectedFileUploader } from '../../../../components/FormElements'
import DataFieldsFieldsGenerator from './DataFieldsFieldsGenerator'
import _ from 'lodash'
import { useGeolocated } from 'react-geolocated'
import { parseDot } from '../../../../utils'
import { PrimaryButton } from '../../../../components/Buttons'
import { ERROR_TOAST, SUCCESS_TOAST } from '../../../../constants'
import { BsFolderPlus } from 'react-icons/bs'
import DataFields from '.'
import { usePreviewerContext } from '../../../../context/PreviewerProvider'
import { useAppContext } from '../../../../context/AppProvider'
import { validationSchema } from '../../utils'
import { captureEvent, errorCode } from '../../../../lib/analytics'

type DataFieldsFormGeneratorProps = {
  task: Maybe<TaskDefinitionEntity>
  evidence?: Maybe<EvidenceEntity>
  editedFrom?: string
}
const DataFieldsFormGenerator: React.FC<DataFieldsFormGeneratorProps> = ({
  task,
  evidence,
  editedFrom
}) => {
  const toast = useToast()
  const { setPreviewComponent, setFormIsDirty } = usePreviewerContext()
  const { toggleDrawer2 } = useAppContext()
  // Ids only: what was reported, never what was entered.
  const reportEventProperties = {
    task_definition_id: task?.id || null,
    project_id: task?.attributes?.project?.data?.id || null,
    is_edit: Boolean(editedFrom)
  }
  React.useEffect(() => {
    if (task?.id) captureEvent('dde_task_report_started', reportEventProperties)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id])
  const [createTask] = useCreateBaoverifierFullTaskFromDesktopMutation({
    onCompleted: () => {
      captureEvent('dde_task_report_submitted', reportEventProperties)
      setPreviewComponent?.({
        title: '',
        icon: BsFolderPlus,
        content: <DataFields task={null} key={Date.now()} />
      })
      setFormIsDirty?.(false)
      toggleDrawer2(false)
      toast({
        ...SUCCESS_TOAST,
        description: 'Task created successfully!'
      })
    },
    onError: (error) => {
      captureEvent('dde_task_report_failed', {
        ...reportEventProperties,
        error_code: errorCode(error)
      })
      console.error('Error creating task:', error)
      toast({
        ...ERROR_TOAST,
        description: 'Failed to create task. Please try again later.'
      })
    }
  })
  const dataFields = task?.attributes?.task_type?.data?.attributes?.dataFields || []

  const evidenceFields = evidence?.attributes?.custom_data_fields || []

  const blankInitialValues = dataFields.reduce((acc, field, index) => {
    const name = `${field?.__typename}_${index}_${parseDot((field as any)?.label || '')}`

    switch (field?.__typename) {
      case 'ComponentCustomDataFieldsText':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsDataList':
        acc[name || ''] = {
          values: [],
          listId: field.listId || null
        }
        break
      case 'ComponentCustomDataFieldsDateTimeField':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsEmailField':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsImageField':
        acc[name || ''] = []
        break

      case 'ComponentCustomDataFieldsNumberField':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsMetricsSpecific':
        acc[name || ''] = {
          value: '',
          metric: field.metric || ''
        }
        break
      case 'ComponentCustomDataFieldsPercentage':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsPhoneNumberField':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsRadio':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsRating':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsSelect':
        acc[name || ''] = ''
        break
      case 'ComponentCustomDataFieldsCheckbox':
        acc[name || ''] = []
        break
      default:
        console.warn(`Unsupported field type: ${field?.__typename}`)
    }
    return acc
  }, {} as Record<string, any>)

  const initialValuesWithEvidence = {
    ...blankInitialValues,
    ...evidenceFields.reduce(
      (acc, field, index) => {
        const name = `${field?.__typename}_${index}_${parseDot((field as any)?.label || '')}`

        switch (field?.__typename) {
          case 'ComponentCustomDataFieldsText':
            acc[name || ''] = field.text_value || ''
            break
          case 'ComponentCustomDataFieldsDataList':
            acc[name || ''] = {
              values: field.option?.map((option) => option?.option) || [],
              listId: field.listId || null
            }
            break
          case 'ComponentCustomDataFieldsDateTimeField':
            acc[name || ''] = field.datetime || ''
            break
          case 'ComponentCustomDataFieldsEmailField':
            acc[name || ''] = field.email || ''
            break
          case 'ComponentCustomDataFieldsImageField':
            acc[name || ''] = field.images?.data || []
            break

          case 'ComponentCustomDataFieldsNumberField':
            acc[name || ''] = field.number_field_value ?? ''
            break
          case 'ComponentCustomDataFieldsMetricsSpecific':
            acc[name || ''] = {
              value: field.number_value ?? '',
              metric: field.metric || ''
            }
            break
          case 'ComponentCustomDataFieldsPercentage':
            acc[name || ''] = field.value ?? ''
            break
          case 'ComponentCustomDataFieldsPhoneNumberField':
            acc[name || ''] = field.phoneNumber || ''
            break
          case 'ComponentCustomDataFieldsRadio':
            acc[name || ''] = field.option?.[0]?.option || ''
            break
          case 'ComponentCustomDataFieldsRating':
            acc[name || ''] = field.rating || 0
            break
          case 'ComponentCustomDataFieldsSelect':
            acc[name || ''] = field.option?.[0]?.option || ''
            break
          case 'ComponentCustomDataFieldsCheckbox':
            acc[name || ''] = field.option?.map((option) => option?.option) || []
            break
          default:
            console.warn(`Unsupported field type: ${field?.__typename}`)
        }
        return acc
      },
      {
        taskImages: evidence?.attributes?.photos?.data || []
      } as Record<string, any>
    )
  }

  const fields = dataFields || []

  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false
    },
    userDecisionTimeout: 5000
  })

  const myLocation = coords
    ? JSON.stringify({
        accuracy: coords.accuracy,
        latitude: coords.latitude,
        longitude: coords.longitude
      })
    : '{}'

  return (
    <Formik
      initialValues={initialValuesWithEvidence}
      isInitialValid={validationSchema(dataFields).isValidSync(initialValuesWithEvidence)}
      validationSchema={validationSchema(dataFields)}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        try {
          console.log('Submitting form with values:', values)
          setSubmitting(true)
          const taskImages = values.taskImages || []
          const fields = _.omitBy(values, (value, key) => key === 'taskImages')

          // Create ordered field names based on dataFields order
          const orderedFieldNames = dataFields.map(
            (field, index) =>
              `${field?.__typename}_${index}_${parseDot((field as any)?.label || '')}`
          )

          const dataLabelValuesArr = orderedFieldNames
            .filter((fieldName) => fieldName !== 'comment')
            .map((fieldName) => {
              const value =
                fields?.[fieldName] !== undefined
                  ? fields[fieldName]
                  : blankInitialValues[fieldName]
              // Number inputs now hold the typed string. Percentage is stored in
              // an integer column, so keep sending it as a number as before;
              // number/metric values are normalised server-side from strings.
              const isPercentage = fieldName.startsWith('ComponentCustomDataFieldsPercentage_')
              return {
                id: fieldName,
                value: isPercentage && value !== '' && value !== null ? Number(value) : value
              }
            })

          const variables = {
            evidence: {
              input: {
                location: myLocation || '',
                photos: taskImages?.map((file: UploadFileEntity) => file.id) || []
              },
              dataLabelValuesArr
            },
            task: {
              status: Enum_Task_Status.Pending,
              project: task?.attributes?.project?.data?.id || null,
              date_reported: new Date().toISOString(),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              baoleader: task?.attributes?.baoleader?.data?.id || null,
              task_definition: task?.id || null
            },
            taskEdited: editedFrom || null
          }

          const { data } = await createTask({
            variables
          })

          if (data?.createBaoverifierFullTaskFromDesktop.task) {
            resetForm()
          }
        } catch (error) {
          console.error('Error submitting form:', error)
        }
        setSubmitting(false)
      }}
    >
      {({ isSubmitting, isValid }) => (
        <Form>
          <DataFieldsFieldsGenerator fields={fields} evidenceFields={evidenceFields} />
          <ConnectedFileUploader
            label="Task Images"
            name="taskImages"
            placeholder="Additional images to support this task"
            isMulti
            autoUpload
            mt={24}
            allowedFileTypes={['image/*']}
          />

          <PrimaryButton
            type="submit"
            mt={4}
            width="100%"
            isLoading={isSubmitting}
            isDisabled={!isValid || isSubmitting}
          >
            Submit
          </PrimaryButton>
          {!isGeolocationAvailable && (
            <Text color="red.500">Your browser does not support Geolocation</Text>
          )}
          {!isGeolocationEnabled && <Text color="red.500">Geolocation is not enabled</Text>}
        </Form>
      )}
    </Formik>
  )
}

export default DataFieldsFormGenerator
